import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ORDER_API, MASTER_API } from "../../../apiConfig";
import { authHeaders } from "../../../utils/authHeaders";
import sbeeLogo from "../../../assets/sbeelogo.png";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

// Seal/Sign files are served as static files off the master-service root
// (e.g. http://localhost:5001/uploads/seal-sign/xyz.jpeg), NOT under the
// "/api" prefix that MASTER_API points to — so strip a trailing "/api"
// (or "/api/...") from MASTER_API to get that root. Same pattern used in
// ProductMaster.js and QuotationView.js.
const SEAL_SIGN_FILE_BASE = MASTER_API.replace(/\/api(\/.*)?$/, "");


const SO_TERMS_DEFAULTS = [
  { term_no: 1, title: "1) Prices:", text: "As mentioned above." },
  { term_no: 2, title: "2)", text: "GST is applicable on the above materials and it is to your account. Presently it is 18%. However it is applicable as on the date of supply. (In case if there is any statutory variations & also introduction of any other taxes/duties etc. at the time of dispatch of materials the same will be applicable to your account at actual.)" },
  { term_no: 4, title: "4) Payment Terms:", text: null, dynamic: true },
  { term_no: 5, title: "5)", text: " Please refer the above table for tolerance details.." },
  { term_no: 6, title: "Note:", text: "Refer to Rate Contract review form for further information.", fixed: true },
];

function SalesOrderView() {
  const { soId } = useParams();
  const [searchParams] = useSearchParams();

  const [soData, setSoData] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [savedTerms, setSavedTerms] = useState([]); // [{term_no, term_text}] overrides from so_terms_conditions

  const [editingTerms, setEditingTerms] = useState(false);
  const [termsDraft, setTermsDraft] = useState({});
  const [savingTerms, setSavingTerms] = useState(false);
  const [unit1Location, setUnit1Location] = useState(null); // from Configuration > Location Master (Unit 1 / Regd. Office) — used for the Order Confirmation Supplier block
  // Seal & Sign records from Configuration > Seal & Sign (master-service),
  // e.g. [{ id, username, file_path }, ...]. Used below to print the
  // approver's actual signature/seal image under "Approved by".
  const [sealSignList, setSealSignList] = useState([]);
  const [usersList, setUsersList] = useState([]); // usererp rows, to resolve a stored full name back to a user id

  const CAL = "Calibri, 'Segoe UI', Arial, sans-serif";
  const ABADI = "Abadi, 'Segoe UI', Calibri, Arial, sans-serif";
  // Company name font — "SBEE CABLES (INDIA) LIMITED" everywhere it appears.
  // "Good Times" isn't a Google/system font, so add an @font-face for it
  // (see note near the print styles below) or it'll fall back silently.
  const GOOD_TIMES = "'Good Times', 'Segoe UI', Calibri, Arial, sans-serif";

  const formatDate = (val) => {
    if (!val) return "-";
    const d = new Date(val);
    if (isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("en-GB"); // dd/mm/yyyy
  };

  const parseSingleValue = (val) => {
    if (!val) return "-";
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) return parsed[0] || "-";
      return String(parsed);
    } catch {
      return String(val);
    }
  };

  // ── Show just the name (before @), first letter capitalized — used for "Prepared by" ──
  const nameFromEmail = (val) => {
    if (!val) return "";
    const namePart = String(val).split("@")[0];
    if (!namePart) return "";
    return namePart.charAt(0).toUpperCase() + namePart.slice(1);
  };

  // ── Load SO header + product lines ──
  const loadSO = async () => {
    try {
      const res = await fetch(`${ORDER_API}/sales-order/accepted-convert/${soId}`, { headers: authHeaders() });
      if (!res.ok) {
        console.error("loadSO Error: request failed with status", res.status);
        return;
      }
      const data = await res.json();
      setSoData(data);

      const customerCode = data?.customer?.split(" - ")[0];
      if (customerCode) {
        try {
          const custRes = await fetch(`${ORDER_API}/quotation/customer-details/${customerCode}`, { headers: authHeaders() });
          if (custRes.ok) setCustomerData(await custRes.json());
        } catch (err) {
          console.error("CUSTOMER DETAILS FETCH ERROR", err);
        }
      }
    } catch (err) {
      console.error("loadSO Error:", err);
    }
  };

  // ── Load saved Terms & Conditions overrides ──
  const loadTerms = async () => {
    try {
      const res = await fetch(`${ORDER_API}/sales-order/${soId}/terms`, { headers: authHeaders() });
      const terms = res.ok ? await res.json() : [];
      setSavedTerms(terms || []);
    } catch (err) {
      console.error("SO TERMS FETCH ERROR", err);
      setSavedTerms([]);
    }
  };

  // ── Load Unit 1 (Regd. Office) address for the Supplier block ──
  const loadUnit1Location = async () => {
    try {
      const res = await fetch(`${MASTER_API}/location-master`, { headers: authHeaders() });
      const data = await res.json();
      const locations = Array.isArray(data) ? data : [];
      const unit1 = locations.find((loc) => (loc.location_label || "").toLowerCase().includes("1")) || null;
      setUnit1Location(unit1);
    } catch (err) {
      console.error("LOCATION MASTER FETCH ERROR", err);
      setUnit1Location(null);
    }
  };

  // ── Load Seal & Sign list (Configuration > Seal & Sign) — used to print
  //    the approver's actual signature/seal image under "Approved by". ──
  const loadSealSignList = async () => {
    try {
      const res = await fetch(`${MASTER_API}/configuration/seal-sign`, { headers: authHeaders() });
      const data = await res.json();
      setSealSignList(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("SEAL SIGN FETCH ERROR", err);
      setSealSignList([]);
    }
    try {
      const uRes = await fetch(`${MASTER_API}/configuration/seal-sign/users`, { headers: authHeaders() });
      const uData = await uRes.json();
      setUsersList(Array.isArray(uData) ? uData : []);
    } catch (err) {
      console.warn("USERS LIST FETCH ERROR", err);
      setUsersList([]);
    }
  };

  useEffect(() => {
    loadSO();
    loadTerms();
    loadUnit1Location();
    loadSealSignList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soId]);

  // Supplier name/address for the Order Confirmation PDF — always Unit 1
  // (Regd. Office), sourced from Configuration > Location Master. Falls back
  // to the static text only if Location Master hasn't been set up yet.
  const supplierName = unit1Location?.company_name || "SBEE CABLES (India) LTD";
  const supplierAddressLine1 = unit1Location
    ? `${unit1Location.address_line1}${unit1Location.address_line2 ? ", " + unit1Location.address_line2 : ""}`
    : "# 282 & 283, KIADB Industrial Area, Harohalli Phase -2,";
  const supplierAddressLine2 = unit1Location
    ? `${unit1Location.city}${unit1Location.pin_code ? " - " + unit1Location.pin_code : ""}, ${unit1Location.state}`
    : "Kanakapura Taluk Ramanagara District – 562112";

  // Matches the approver (soData.approved_by, which may be a bare username
  // OR a full email like "maithili@company.com") against the Seal & Sign
  // list's `username` column, case-insensitively and ignoring the email
  // domain, so it works with however the SO stores the value.
  const approverSealUrl = (() => {
    const approver = soData?.approved_by;
    if (!approver || !sealSignList.length) return null;
    const approverKey = String(approver).split("@")[0].trim().toLowerCase();
    // approved_by is now stored as the person's full name ("Maithili M"),
    // but Seal & Sign records are keyed by user id — so resolve the name
    // to that user's id/username via the users list as well.
    const keys = new Set([approverKey]);
    usersList.forEach((u) => {
      const fullName = `${u.first_name || ""} ${u.last_name || ""}`.trim();
      const candidates = [u.user_id, u.username, u.name, fullName]
        .filter(Boolean)
        .map((c) => String(c).split("@")[0].trim().toLowerCase());
      if (candidates.includes(approverKey)) {
        [u.user_id, u.username].filter(Boolean).forEach((k) => keys.add(String(k).trim().toLowerCase()));
      }
    });
    const match = sealSignList.find((s) =>
      keys.has(String(s.username || "").trim().toLowerCase())
    );
    return match?.file_path ? `${SEAL_SIGN_FILE_BASE}${match.file_path}` : null;
  })();

  useEffect(() => {
    if (!soData) return;

    if (searchParams.get("download") === "true") {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => downloadPDF(), 600);
        });
      });
    }

    if (searchParams.get("print") === "true") {
      setTimeout(() => window.print(), 1200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soData, searchParams]);

  // ── Terms & Conditions helpers ──
  const getDefaultTermText = (term_no) => {
    if (term_no === 4) return soData?.payment_terms || "-";
    return SO_TERMS_DEFAULTS.find((t) => t.term_no === term_no)?.text || "";
  };

  const getEffectiveTermText = (term_no) => {
    const rowDef = SO_TERMS_DEFAULTS.find((t) => t.term_no === term_no);
    // dynamic (Payment Terms, pulled from the SO) and fixed (never
    // user-editable) terms always resolve to their computed/default text —
    // never a saved override, even a stale one from before this term was
    // marked fixed.
    if (rowDef?.dynamic || rowDef?.fixed) return getDefaultTermText(term_no);

    const override = (savedTerms || []).find((t) => Number(t.term_no) === term_no);
    return override ? override.term_text : getDefaultTermText(term_no);
  };

  const handleEditTerms = () => {
    const draft = {};
    SO_TERMS_DEFAULTS.forEach((t) => {
      if (t.dynamic || t.fixed) return;
      draft[t.term_no] = getEffectiveTermText(t.term_no);
    });
    setTermsDraft(draft);
    setEditingTerms(true);
  };

  const handleCancelTerms = () => {
    setEditingTerms(false);
    setTermsDraft({});
  };

  const handleSaveTerms = async () => {
    try {
      setSavingTerms(true);
      const terms = SO_TERMS_DEFAULTS
        .filter((t) => !t.dynamic && !t.fixed)
        .map((t) => ({
          term_no: t.term_no,
          term_text: (termsDraft[t.term_no] ?? "").toString(),
        }));

      const res = await fetch(`${ORDER_API}/sales-order/${soId}/terms`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ terms }),
      });

      if (!res.ok) throw new Error("Failed to save terms");

      setSavedTerms(terms);
      setEditingTerms(false);
    } catch (err) {
      console.error("saveTerms Error:", err);
      alert("Failed to save Terms & Conditions. Please try again.");
    } finally {
      setSavingTerms(false);
    }
  };

  // ── PDF export — same clone/off-screen/paginate approach as QuotationView ──
  const downloadPDF = async () => {
    let clone = null;
    try {
      const input = document.getElementById("salesorder-container");
      if (!input) return;

      let logoBase64 = null;
      await new Promise((resolve) => {
        const tempImg = new Image();
        tempImg.onload = () => {
          try {
            const cvs = document.createElement("canvas");
            cvs.width = tempImg.naturalWidth || 440;
            cvs.height = tempImg.naturalHeight || 200;
            cvs.getContext("2d").drawImage(tempImg, 0, 0);
            logoBase64 = cvs.toDataURL("image/png");
          } catch (e) {
            // canvas taint — leave null, original src will be used
          }
          resolve();
        };
        tempImg.onerror = resolve;
        tempImg.src = sbeeLogo;
      });

      clone = input.cloneNode(true);
      clone.style.position = "absolute";
      clone.style.top = "-9999px";
      clone.style.left = "0";
      clone.style.width = "1050px";
      clone.style.margin = "0";
      clone.style.visibility = "visible";
      clone.style.background = "#ffffff";
      document.body.appendChild(clone);

      // Strip edit-only controls; convert any live <textarea> to plain text
      clone.querySelectorAll(".no-pdf").forEach((el) => el.remove());
      clone.querySelectorAll("textarea").forEach((ta) => {
        const div = document.createElement("div");
        div.textContent = ta.value;
        div.style.cssText = `font-family:${ABADI}; font-size:13px; white-space:pre-wrap;`;
        ta.parentNode.replaceChild(div, ta);
      });

      if (logoBase64) {
        clone.querySelectorAll("img").forEach((img) => (img.src = logoBase64));
      }

      const cloneTop = clone.getBoundingClientRect().top;
      const rowBoundaries = Array.from(clone.querySelectorAll("tr, li")).map((el) => {
        const r = el.getBoundingClientRect();
        return { top: r.top - cloneTop, bottom: r.bottom - cloneTop };
      });

      await new Promise((r) => setTimeout(r, 800));

      const canvas = await html2canvas(clone, {
        useCORS: false,
        allowTaint: true,
        scale: 2,
        backgroundColor: "#ffffff",
        imageTimeout: 15000,
        logging: false,
      });

      const A4_W = 210;
      const A4_H = 297;
      const imgW = A4_W;
      const pxPerMm = canvas.width / A4_W;
      const pageHeightPx = A4_H * pxPerMm;
      const rowBoundariesPx = rowBoundaries.map((b) => ({
        top: b.top * pxPerMm,
        bottom: b.bottom * pxPerMm,
      }));

      const pdf = new jsPDF("p", "mm", "a4");
      let cursorPx = 0;
      let firstPage = true;
      const totalPx = canvas.height;

      while (cursorPx < totalPx - 1) {
        const idealEnd = cursorPx + pageHeightPx;
        let cutPx = idealEnd;

        if (idealEnd < totalPx) {
          let bestBoundary = null;
          for (const b of rowBoundariesPx) {
            if (b.bottom > cursorPx && b.bottom <= idealEnd) {
              if (!bestBoundary || b.bottom > bestBoundary) bestBoundary = b.bottom;
            }
          }
          if (bestBoundary && bestBoundary > cursorPx + 1) cutPx = bestBoundary;
        }

        const sliceHeightPx = Math.min(cutPx, totalPx) - cursorPx;
        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvas.width;
        pageCanvas.height = sliceHeightPx;
        const ctx = pageCanvas.getContext("2d");
        ctx.drawImage(canvas, 0, cursorPx, canvas.width, sliceHeightPx, 0, 0, canvas.width, sliceHeightPx);

        const sliceHeightMm = sliceHeightPx / pxPerMm;

        if (!firstPage) pdf.addPage();
        pdf.addImage(pageCanvas.toDataURL("image/png"), "PNG", 0, 0, imgW, sliceHeightMm);

        cursorPx += sliceHeightPx;
        firstPage = false;
      }

      pdf.save(soId + "_OrderConfirmation.pdf");

      if (searchParams.get("download") === "true") {
        setTimeout(() => window.close(), 1500);
      }
    } catch (err) {
      console.error("PDF Error:", err);
      alert("PDF generation failed. Please try again.");
    } finally {
      if (clone && clone.parentNode) clone.parentNode.removeChild(clone);
    }
  };

  if (!soData) {
    return <div style={{ padding: 40, fontFamily: CAL }}>Loading Order Confirmation…</div>;
  }

  const orderDate = formatDate(soData.creation_date || soData.created_at);
  const customerName = (soData.customer || "-").split(" - ").slice(1).join(" - ") || soData.customer || "-";
  const customerAddress =
    [
      customerData?.billing_line1,
      customerData?.billing_line2,
      customerData?.billing_city,
      customerData?.billing_state,
      customerData?.billing_zip,
    ]
      .filter(Boolean)
      .join(", ") || "-";
  const customerEmail =
    parseSingleValue(customerData?.email || customerData?.Email || customerData?.contact_email) || "-";
  const attn =
    customerData?.contact_person_name || customerData?.contact_person || customerData?.attn || "-";

  // ── Business Partner Responsible / Phone / Email — captured on the
  //    customer's Business Partner "Basic Info" tab, pulled through here
  //    via customerData (same source as customerName/customerAddress). ──
  const responsibleName = customerData?.business_partner_responsible || "-";
  const responsiblePhone = customerData?.partner_responsible_phone || "-";
  const responsibleEmail = customerData?.partner_responsible_email || "-";

  // Order Confirmation only ever covers what was actually approved in
  // Review — a rejected line item shouldn't appear on the document sent
  // to the customer. Falls back to showing everything if so_action isn't
  // present at all (e.g. an older SO saved before per-item review existed).
  const allRows = soData.productDetails || [];
  const hasReviewData = allRows.some((r) => r.so_action);
  const rows = hasReviewData
    ? allRows.filter((r) => {
        const s = (r.so_action || "").toLowerCase();
        return s === "approved" || s === "cancellation approved";
      })
    : allRows;

  // ── Same per-line net-price computation used in QuotationView.js:
  //    prefer the saved net_price_after_discount / discounted_price if
  //    present, otherwise derive from unit_price * qty minus discount%. ──
  const lineNet = (row) => {
    const qty        = parseFloat(row.quantity   || 0);
    const unitPrice  = parseFloat(row.unit_price || 0);
    const disc       = parseFloat(row.discount   || 0);
    const gross      = unitPrice * qty;
    const discounted = row.discounted_price
      ? parseFloat(row.discounted_price)
      : gross - (disc * gross) / 100;
    return row.net_price_after_discount
      ? parseFloat(row.net_price_after_discount)
      : discounted;
  };

  const fmtN = (v) => {
    const n = Number(v);
    if (isNaN(n)) return "0.00";
    return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div
      id="salesorder-container"
      style={{
        background: "#fff",
        padding: "40px 50px",
        width: "1050px",
        margin: "0 auto",
        fontFamily: CAL,
        fontSize: "14px",
        color: "#000",
        lineHeight: "1.5",
        boxSizing: "border-box",
      }}
    >
      {/* Hides anything marked .no-pdf (toolbar buttons, Edit/Save/Cancel)
          when the browser's native print dialog is used — window.print()
          doesn't go through the html2canvas clone, so it needs its own
          print-only rule to strip these controls. */}
      <style>{`
        @media print {
          .no-pdf {
            display: none !important;
          }
        }
      `}</style>

      {/* ── Toolbar (stripped from PDF export via .no-pdf) ── */}
      <div className="no-pdf" style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12, gap: 8 }}>
        <button
          type="button"
          onClick={downloadPDF}
          style={{
            padding: "6px 14px", fontSize: "13px", fontFamily: CAL,
            background: "#0f172a", color: "#fff", border: "none",
            borderRadius: "4px", cursor: "pointer",
          }}
        >
          Download PDF
        </button>
      </div>

      {/* ── Letterhead — logo top-right, matches Quotation format ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "6px" }}>
        <p style={{ fontSize: "14px", fontStyle: "italic", margin: 0, color: "#100e0e" }}>
          MKG/F-02 Rev.No.01 Dtd 01.01.13.
        </p>
        <img src={sbeeLogo} alt="SBEE" style={{ height: "50px", objectFit: "contain" }} />
      </div>

      {/* ── Title bar ── */}
      <table cellPadding="0" cellSpacing="0" width="100%" style={{ borderCollapse: "collapse", marginBottom: "12px" }}>
        <tbody>
          <tr>
            <td
              style={{
                border: "1px solid #000",
                textAlign: "center",
                fontWeight: "bold",
                fontSize: "16px",
                padding: "10px",
              }}
            >
              ORDER CONFIRMATION NO: {soData.so_id || "-"}
            </td>
            <td style={{ border: "1px solid #000", padding: "10px", width: "160px", verticalAlign: "top" }}>
              <strong>Date:</strong> {orderDate}
            </td>
          </tr>
        </tbody>
      </table>

      {/* ── Supplier block ── */}
      <table cellPadding="0" cellSpacing="0" width="100%" style={{ borderCollapse: "collapse", marginBottom: "10px" }}>
        <tbody>
          <tr>
            <td style={{ border: "1px solid #000", padding: "10px" }}>
              <p style={{ color: "#c00000", fontWeight: "bold", margin: "0 0 4px 0" }}>
                Supplier: <span style={{ fontFamily: GOOD_TIMES }}>{supplierName}</span>
              </p>
              <p style={{ margin: "0 0 4px 0" }}>
                {supplierAddressLine1}
              </p>
              <p style={{ margin: "0 0 4px 0" }}>{supplierAddressLine2}</p>
              <p style={{ margin: 0, color: "#0563c1", textDecoration: "underline" }}>
                Email: info@sbeecables.in
              </p>
            </td>
          </tr>
        </tbody>
      </table>

      {/* ── Customer block ── */}
      <table cellPadding="0" cellSpacing="0" width="100%" style={{ borderCollapse: "collapse", marginBottom: "10px" }}>
        <tbody>
          <tr>
            <td style={{ border: "1px solid #000", padding: "10px" }}>
              <p style={{ fontWeight: "bold", margin: "0 0 4px 0" }}>Customer: {customerName}</p>
              <p style={{ margin: "0 0 4px 0" }}>Address: {customerAddress}</p>
              <p style={{ margin: 0, color: "#0563c1", textDecoration: "underline" }}>
                Email: {customerEmail}
              </p>
            </td>
          </tr>
        </tbody>
      </table>

      {/* ── Quotation No. / Rate Contract No. — RC Driven SOs show the RC
           Number instead of the Quotation No, since the field is mutually
           exclusive with quotation_type (see quotation_type_locked logic
           in sales_order_service.js). Falls back to the RFQ ID as the
           Quotation No since that's what drives a Quotation Driven SO. ── */}
      <p style={{ margin: "0 0 4px 0" }}>
        {soData.quotation_type !== "RC Driven" && <>Quotation No: {soData.rfq_id || "-"}</>}
      </p>

      <p style={{ margin: "0 0 10px 0" }}>Kind Attn: Mr/Ms {attn !== "-" ? attn : ""}</p>

      {/* ── Line items table ── */}
      <table
        cellPadding="0"
        cellSpacing="0"
        width="100%"
        style={{ borderCollapse: "collapse", border: "1px solid #000", marginBottom: "16px" }}
      >
        <thead>
          <tr style={{ background: "#f1f5f9" }}>
           {[
  "Sl No",
  "YOUR P.O.NO. & PO Date",
  "PRODUCT CODE",
  "CABLES 'SBEE' MAKE AS UNDER",
  "UOM",
  "QTY",
  "UNIT PRICE (₹ / unit)",
  "TOTAL",
  "TOLERANCE ON Qty",
  "YOUR REQUIRED DEL DATE",
  "OUR EXPECTED DISPATCH DATE",
].map((h) => (
              <th
                key={h}
                style={{ border: "1px solid #000", padding: "6px 4px", fontSize: "11px", fontFamily: ABADI, textAlign: "center" }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.id || i}>
              <td style={{ border: "1px solid #000", padding: "6px 4px", textAlign: "center", fontSize: "12px" }}>{i + 1}</td>
              <td
  style={{
    border: "1px solid #000",
    padding: "6px 4px",
    textAlign: "center",
    fontSize: "12px",
  }}
>
  <div>{soData.purchaseOrder || "-"}</div>
  <div>{formatDate(String(soData.purchase_order_date || "").replace(" ", "T"))}</div>
</td>

<td
  style={{
    border: "1px solid #000",
    padding: "6px 4px",
    textAlign: "center",
    fontSize: "12px",
  }}
>
  {row.product_code || row.productCode || "-"}
</td>

<td
  style={{
    border: "1px solid #000",
    padding: "6px 4px",
    fontSize: "12px",
  }}
>
  {row.description || "-"}
</td>
              <td style={{ border: "1px solid #000", padding: "6px 4px", textAlign: "center", fontSize: "12px" }}>
                {row.uom || "Mtr"}
              </td>
              <td style={{ border: "1px solid #000", padding: "6px 4px", textAlign: "right", fontSize: "12px" }}>
                {row.quantity ?? "-"}
              </td>
              <td style={{ border: "1px solid #000", padding: "6px 4px", textAlign: "right", fontSize: "12px" }}>
                {row.unit_price ?? "-"}
              </td>
              <td style={{ border: "1px solid #000", padding: "6px 4px", textAlign: "right", fontSize: "12px", fontWeight: 700 }}>
                {fmtN(lineNet(row))}
              </td>
              {(() => {
                // ── Tolerance on Length — same logic as the Quotation PDF:
                //    pulled from this line's Characteristics (Over Tolerance /
                //    Under Tolerance). Shown as "+X%" / "-Y%" on their own
                //    lines, with a note on whether it's applied on batch
                //    length (tolerance_applied checked) or on Qty. ──
                const chars = row.characteristics || [];
                const findChar = (name) =>
                  chars.find((c) => (c.characteristic || "").trim().toLowerCase() === name);
                const overTol  = findChar("over tolerance");
                const underTol = findChar("under tolerance");

                let toleranceValueText = "-";
                let toleranceApplyText = "";
                if (overTol || underTol) {
                  const overVal  = overTol  ? parseFloat(overTol.value)  : null;
                  const underVal = underTol ? parseFloat(underTol.value) : null;

                  const parts = [];
                  if (overVal !== null && !isNaN(overVal))  parts.push(`+${overVal}%`);
                  if (underVal !== null && !isNaN(underVal)) parts.push(`-${underVal}%`);

                  const isBatchLength =
                    (overTol?.tolerance_applied === 1 || overTol?.tolerance_applied === true) ||
                    (underTol?.tolerance_applied === 1 || underTol?.tolerance_applied === true);

                  if (parts.length) {
                    toleranceValueText = parts.join(", ");
                    toleranceApplyText = `(Applied on ${isBatchLength ? "batch length" : "Order Qty"})`;
                  }
                }

                return (
                  <td style={{ border: "1px solid #000", padding: "6px 4px", textAlign: "center", fontSize: "11px", whiteSpace: "nowrap" }}>
                    <div>{toleranceValueText}</div>
                    {toleranceApplyText && <div>{toleranceApplyText}</div>}
                  </td>
                );
              })()}
              <td style={{ border: "1px solid #000", padding: "6px 4px", textAlign: "center", fontSize: "12px" }}>
                {formatDate(row.requirement_date)}
              </td>
              <td style={{ border: "1px solid #000", padding: "6px 4px", textAlign: "center", fontSize: "12px" }}>
                {formatDate(row.confirmation_date || row.tentative_conf_date)}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={11} style={{ border: "1px solid #000", padding: "10px", textAlign: "center", color: "#888" }}>
                No line items found for this Sales Order.
              </td>
            </tr>
          )}

          {/* ── CGST/SGST/IGST/TOTAL footer — per-line net (net_price_after_discount,
                 else unit_price*qty minus discount%) summed, with each tax
                 broken into its own row (rather than one combined "GST" row)
                 plus grand total (final_price_after_gst, else net+GST). Kept
                 inside <tbody> (not <tfoot>) so it survives the html2canvas
                 PDF export the same way the Quotation table does. ── */}
          {rows.length > 0 && (() => {
            const totals = rows.reduce(
              (acc, row) => {
                const net      = lineNet(row);
                const cgst     = parseFloat(row.cgst_amount || 0);
                const sgst     = parseFloat(row.sgst_amount || 0);
                const igst     = parseFloat(row.igst_amount || 0);
                const totalGst = parseFloat(row.total_gst || 0) || (cgst + sgst + igst);
                const grand    = parseFloat(row.final_price_after_gst || 0) || (net + totalGst);
                return {
                  net:      acc.net      + net,
                  cgst:     acc.cgst     + cgst,
                  sgst:     acc.sgst     + sgst,
                  igst:     acc.igst     + igst,
                  totalGst: acc.totalGst + totalGst,
                  grand:    acc.grand    + grand,
                };
              },
              { net: 0, cgst: 0, sgst: 0, igst: 0, totalGst: 0, grand: 0 }
            );

            // ── Rate shown next to each label (e.g. "CGST @9%") — only when
            //    every line with a non-zero amount for that tax shares the
            //    SAME rate; otherwise the % is omitted for that row. ──
            const uniformRate = (amountKey, rateKey) => {
              const rates = rows
                .filter((row) => parseFloat(row[amountKey] || 0) > 0)
                .map((row) => parseFloat(row[rateKey] || 0));
              if (rates.length === 0) return null;
              const allSame = rates.every((r) => r === rates[0]);
              return allSame ? rates[0] : null;
            };
            const cgstRate = uniformRate("cgst_amount", "cgst");
            const sgstRate = uniformRate("sgst_amount", "sgst");
            const igstRate = uniformRate("igst_amount", "igst");

            const currencyLabel = rows?.[0]?.currency || "INR";

            const footTd = {
              border: "1px solid #000",
              padding: "8px 4px",
              fontFamily: CAL,
              fontSize: "13px",
              fontWeight: 700,
              backgroundColor: "#f1f5f9",
              color: "#000",
              textAlign: "right",
              whiteSpace: "nowrap",
            };

            const taxLabel = (name, rate) =>
              rate !== null && rate !== undefined ? `${name} @${rate}%` : name;

            return (
              <>
                {/* ── SUBTOTAL ROW — sum of all line net totals, before GST ── */}
                <tr>
                  <td colSpan={7} style={{ ...footTd, textAlign: "right", fontWeight: 800, fontSize: "18px", color: "#333", paddingRight: "8px" }}>
                    {currencyLabel} ₹
                  </td>
                  <td style={{ ...footTd, textAlign: "right" }}>
                    Sub Total: {fmtN(totals.net)}
                  </td>
                  <td style={footTd} colSpan={3}></td>
                </tr>
                {(totals.cgst > 0 || totals.sgst > 0 || totals.igst > 0) && (
                  <tr>
                    <td colSpan={7} style={{ ...footTd, textAlign: "left", fontWeight: 600, color: "#333", verticalAlign: "top" }}>
                    </td>
                    {/* Mini two-column table so labels stay left and every
                        amount lines up in a single right-aligned column,
                        regardless of label length ("CGST @9%" vs "Total GST"). */}
                    <td style={{ ...footTd, textAlign: "left", padding: 0 }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <tbody>
                          {totals.cgst > 0 && (
                            <tr>
                              <td style={{ border: "none", padding: "2px 4px", textAlign: "left", fontFamily: CAL, fontSize: "13px", fontWeight: 700, color: "#000" }}>
                                {taxLabel("CGST", cgstRate)}
                              </td>
                              <td style={{ border: "none", padding: "2px 4px", textAlign: "right", fontFamily: CAL, fontSize: "13px", fontWeight: 700, color: "#000", whiteSpace: "nowrap" }}>
                                {fmtN(totals.cgst)}
                              </td>
                            </tr>
                          )}
                          {totals.sgst > 0 && (
                            <tr>
                              <td style={{ border: "none", padding: "2px 4px", textAlign: "left", fontFamily: CAL, fontSize: "13px", fontWeight: 700, color: "#000" }}>
                                {taxLabel("SGST", sgstRate)}
                              </td>
                              <td style={{ border: "none", padding: "2px 4px", textAlign: "right", fontFamily: CAL, fontSize: "13px", fontWeight: 700, color: "#000", whiteSpace: "nowrap" }}>
                                {fmtN(totals.sgst)}
                              </td>
                            </tr>
                          )}
                          {totals.igst > 0 && (
                            <tr>
                              <td style={{ border: "none", padding: "2px 4px", textAlign: "left", fontFamily: CAL, fontSize: "13px", fontWeight: 700, color: "#000" }}>
                                {taxLabel("IGST", igstRate)}
                              </td>
                              <td style={{ border: "none", padding: "2px 4px", textAlign: "right", fontFamily: CAL, fontSize: "13px", fontWeight: 700, color: "#000", whiteSpace: "nowrap" }}>
                                {fmtN(totals.igst)}
                              </td>
                            </tr>
                          )}
                          <tr>
                            <td style={{ border: "none", padding: "2px 4px", textAlign: "left", fontFamily: CAL, fontSize: "13px", fontWeight: 700, color: "#000" }}>
                              Total GST
                            </td>
                            <td style={{ border: "none", padding: "2px 4px", textAlign: "right", fontFamily: CAL, fontSize: "13px", fontWeight: 700, color: "#000", whiteSpace: "nowrap" }}>
                              {fmtN(totals.totalGst)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                    <td style={footTd} colSpan={3}></td>
                  </tr>
                )}
                <tr>
                  <td colSpan={7} style={{ ...footTd, textAlign: "left", fontWeight: 600, color: "#333" }}>
                  </td>
                  <td style={{ ...footTd, textAlign: "right", fontSize: "14px", color: "#0f172a", fontWeight: 800 }}>
                    TOTAL: {fmtN(totals.grand)}
                  </td>
                  <td style={footTd} colSpan={3}></td>
                </tr>
              </>
            );
          })()}
        </tbody>
      </table>

      {/* ── Terms & Conditions ── */}
      <div style={{ marginTop: "10px" }}>
        <div className="no-pdf" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
          <p style={{ fontFamily: CAL, fontSize: "16px", fontWeight: "bold", margin: 0 }}>Terms &amp; Conditions</p>

          {!editingTerms ? (
            <button
              type="button"
              onClick={handleEditTerms}
              style={{
                padding: "6px 14px", fontSize: "13px", fontFamily: CAL,
                background: "#0f172a", color: "#fff", border: "none",
                borderRadius: "4px", cursor: "pointer",
              }}
            >
              ✎ Edit
            </button>
          ) : (
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                type="button"
                onClick={handleCancelTerms}
                disabled={savingTerms}
                style={{
                  padding: "6px 14px", fontSize: "13px", fontFamily: CAL,
                  background: "#fff", color: "#333", border: "1px solid #ccc",
                  borderRadius: "4px", cursor: savingTerms ? "not-allowed" : "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveTerms}
                disabled={savingTerms}
                style={{
                  padding: "6px 14px", fontSize: "13px", fontFamily: CAL,
                  background: "#16a34a", color: "#fff", border: "none",
                  borderRadius: "4px", cursor: savingTerms ? "not-allowed" : "pointer",
                }}
              >
                {savingTerms ? "Saving…" : "Save"}
              </button>
            </div>
          )}
        </div>

        {/* Plain-text layout on purpose — the approved MKG/F-02 template
            renders Terms & Conditions as flowing paragraph lines, not a
            bordered table (unlike the Quotation format). */}
        {!editingTerms ? (
          <p style={{ fontFamily: CAL, fontSize: "13px", margin: "0 0 4px 0" }}>
            {SO_TERMS_DEFAULTS[0].title} {getEffectiveTermText(1)}
          </p>
        ) : (
          <div style={{ display: "flex", gap: "6px", marginBottom: "4px", alignItems: "flex-start" }}>
            <span style={{ fontSize: "13px", whiteSpace: "nowrap", paddingTop: "6px" }}>{SO_TERMS_DEFAULTS[0].title}</span>
            <textarea
              value={termsDraft[1] ?? ""}
              onChange={(e) => setTermsDraft((prev) => ({ ...prev, 1: e.target.value }))}
              rows={2}
              style={{ flex: 1, fontFamily: ABADI, fontSize: "13px", border: "1px solid #94a3b8", borderRadius: "4px", padding: "6px", resize: "vertical" }}
            />
          </div>
        )}

        <p style={{ fontFamily: CAL, fontSize: "13px", fontWeight: "bold", margin: "8px 0 4px 0" }}>Taxes &amp; Duties</p>

        {!editingTerms ? (
          <p style={{ fontFamily: CAL, fontSize: "13px", margin: "0 0 4px 0" }}>
            {SO_TERMS_DEFAULTS[1].title} {getEffectiveTermText(2)}
          </p>
        ) : (
          <div style={{ display: "flex", gap: "6px", marginBottom: "4px", alignItems: "flex-start" }}>
            <span style={{ fontSize: "13px", whiteSpace: "nowrap", paddingTop: "6px" }}>{SO_TERMS_DEFAULTS[1].title}</span>
            <textarea
              value={termsDraft[2] ?? ""}
              onChange={(e) => setTermsDraft((prev) => ({ ...prev, 2: e.target.value }))}
              rows={3}
              style={{ flex: 1, fontFamily: ABADI, fontSize: "13px", border: "1px solid #94a3b8", borderRadius: "4px", padding: "6px", resize: "vertical" }}
            />
          </div>
        )}

        <p style={{ fontFamily: CAL, fontSize: "13px", margin: "0 0 4px 0" }}>
          {SO_TERMS_DEFAULTS[2].title} {getEffectiveTermText(4)}
          {editingTerms && (
            <span style={{ fontSize: "11px", color: "#64748b", fontStyle: "italic", marginLeft: "6px" }}>
              (Set on the Sales Order form — not editable here.)
            </span>
          )}
        </p>

        {!editingTerms ? (
          <p style={{ fontFamily: CAL, fontSize: "13px", fontWeight: "bold", margin: "0 0 4px 0" }}>
            {SO_TERMS_DEFAULTS[3].title} {getEffectiveTermText(5)}
          </p>
        ) : (
          <div style={{ display: "flex", gap: "6px", marginBottom: "4px", alignItems: "flex-start" }}>
            <span style={{ fontSize: "13px", fontWeight: "bold", whiteSpace: "nowrap", paddingTop: "6px" }}>{SO_TERMS_DEFAULTS[3].title}</span>
            <textarea
              value={termsDraft[5] ?? ""}
              onChange={(e) => setTermsDraft((prev) => ({ ...prev, 5: e.target.value }))}
              rows={2}
              style={{ flex: 1, fontFamily: ABADI, fontSize: "13px", fontWeight: "bold", border: "1px solid #94a3b8", borderRadius: "4px", padding: "6px", resize: "vertical" }}
            />
          </div>
        )}

        {/* Fixed note — never editable, even in edit mode (matches the
            "not editable here" Payment Terms row above). */}
        <p style={{ fontFamily: CAL, fontSize: "13px", margin: "12px 0 4px 0" }}>
          {getEffectiveTermText(6)}
        </p>

        <p style={{ fontFamily: CAL, fontSize: "13px", fontWeight: "bold", margin: "10px 0 4px 0" }}>
          For any queries please contact the undersigned
        </p>

        <p style={{ fontFamily: CAL, fontSize: "13px", margin: "20px 0 0 0" }}>Thanks &amp; Regards,</p>


        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginTop: "10px",
            fontFamily: CAL,
            fontSize: "13px"
          }}
        >
          <div>
            <b>Prepared by :</b> {nameFromEmail(soData?.created_by)}
          </div>

          <div style={{ textAlign: "right" }}>
            <div>
              <b>Approved by :</b> {nameFromEmail(soData?.approved_by)}
            </div>
            {approverSealUrl && (
              <div style={{ marginTop: "6px" }}>
                <img
                  src={approverSealUrl}
                  alt="Seal & Sign"
                  crossOrigin="anonymous"
                  style={{ maxHeight: "70px", maxWidth: "160px", objectFit: "contain" }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SalesOrderView;